import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/high-risk-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

const HIGH_RISK_CONDITIONS = [
  { condition: 'Type 2 Diabetes (controlled)', rateImpact: '+40–100%', bestCarrier: 'Pacific Life', option: 'Standard to Table 2', available: true },
  { condition: 'Type 1 Diabetes (controlled)', rateImpact: '+75–200%', bestCarrier: 'Pacific Life', option: 'Table 2–6', available: true },
  { condition: 'High Blood Pressure (controlled)', rateImpact: '+15–40%', bestCarrier: 'Banner Life', option: 'Standard Plus to Standard', available: true },
  { condition: 'Heart Disease (stable, 2+ yrs)', rateImpact: '+100–300%', bestCarrier: 'Prudential', option: 'Table 4–8', available: true },
  { condition: 'Cancer (in remission 5+ yrs)', rateImpact: '+50–200%', bestCarrier: 'Prudential', option: 'Standard to Table 4', available: true },
  { condition: 'Active Cancer', rateImpact: 'Decline or GI only', bestCarrier: 'Mutual of Omaha', option: 'Guaranteed Issue', available: true },
  { condition: 'COPD / Emphysema (mild)', rateImpact: '+75–200%', bestCarrier: 'Prudential', option: 'Table 2–6', available: true },
  { condition: 'Obesity (BMI 35–40)', rateImpact: '+25–75%', bestCarrier: 'Banner Life', option: 'Standard to Table 2', available: true },
  { condition: 'Obesity (BMI 40+)', rateImpact: '+75–200%', bestCarrier: 'Prudential', option: 'Table 2–6', available: true },
  { condition: 'Stroke (2+ years ago)', rateImpact: '+100–400%', bestCarrier: 'Prudential', option: 'Table 4–10', available: true },
  { condition: 'Kidney Disease (stage 1–2)', rateImpact: '+50–150%', bestCarrier: 'Pacific Life', option: 'Standard to Table 4', available: true },
  { condition: 'Kidney Failure / Dialysis', rateImpact: 'Decline or GI only', bestCarrier: 'Mutual of Omaha', option: 'Guaranteed Issue', available: true },
  { condition: 'HIV (undetectable viral load)', rateImpact: '+100–300%', bestCarrier: 'Prudential', option: 'Table 4–8', available: true },
  { condition: 'Bipolar Disorder (stable)', rateImpact: '+50–150%', bestCarrier: 'Protective Life', option: 'Standard to Table 4', available: true },
  { condition: 'Hazardous Occupation', rateImpact: '+25–100%', bestCarrier: 'Banner Life', option: 'Standard Plus to Table 2', available: true },
];

const CLUSTER_LINKS = [
  { to: '/life-insurance-for-diabetics-indianapolis', label: 'Life Insurance for Diabetics Indianapolis', icon: 'ri-heart-pulse-line' },
  { to: '/life-insurance-smokers-indianapolis', label: 'Life Insurance for Smokers Indianapolis', icon: 'ri-forbid-line' },
  { to: '/guaranteed-issue-life-insurance-indianapolis', label: 'Guaranteed Issue Life Insurance Indianapolis', icon: 'ri-shield-check-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/life-insurance-for-seniors-over-60-indianapolis', label: 'Life Insurance for Seniors Over 60', icon: 'ri-calendar-line' },
  { to: '/senior-life-insurance-indianapolis', label: 'Senior Life Insurance Indianapolis', icon: 'ri-user-heart-line' },
  { to: '/final-expense-insurance-quotes-indianapolis', label: 'Final Expense Insurance Quotes Indianapolis', icon: 'ri-heart-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/life-insurance-comparison-indianapolis', label: 'Life Insurance Comparison Indianapolis', icon: 'ri-scales-3-line' },
];

const FAQS = [
  {
    q: 'Can I get life insurance in Indianapolis with a serious health condition?',
    a: 'Yes — most Indianapolis residents with serious health conditions can still get life insurance. The key is working with an independent agent who knows which carriers are most lenient with specific conditions. Here\'s how it works: Traditional life insurance (fully underwritten): Available for most controlled conditions. Rates are higher than standard, but coverage is available. Simplified issue life insurance: No medical exam, limited health questions. Available for many conditions that would be declined for traditional coverage. Guaranteed issue life insurance: No health questions, guaranteed approval for all Indianapolis applicants ages 45–85. Available for any condition, including active cancer, dialysis, and terminal illness. The most important step: don\'t assume you\'re uninsurable. Call (317) 648-2983 for a free consultation — we\'ve helped hundreds of Indianapolis residents with health conditions find affordable coverage.',
  },
  {
    q: 'What is a table rating in life insurance?',
    a: 'A table rating is a surcharge applied to your life insurance premium when you have a health condition that increases your risk above the standard rate class. Table ratings are numbered 1–10 (or A–J), with each table adding approximately 25% to the standard premium. Example: Standard rate for $500K 20-year term at age 40: $55/month. Table 2 rating (50% surcharge): $83/month. Table 4 rating (100% surcharge): $110/month. Table 8 rating (200% surcharge): $165/month. Table ratings are not permanent — if your health improves, you can apply for a better rate class. An independent agent can shop your case to multiple carriers to find the lowest table rating for your specific condition.',
  },
  {
    q: 'What is the best life insurance carrier for high-risk applicants in Indianapolis?',
    a: 'The best life insurance carrier for high-risk Indianapolis applicants depends on your specific condition. Here are the top carriers by condition: Diabetes (Type 1 or 2): Pacific Life — most lenient underwriting for diabetics. Heart disease: Prudential — best for cardiac conditions. Cancer (in remission): Prudential — most experience with cancer survivors. COPD / emphysema: Prudential — best for respiratory conditions. Obesity: Banner Life — most lenient BMI guidelines. Mental health conditions: Protective Life — most lenient for bipolar, depression, anxiety. HIV: Prudential — one of the few carriers that accepts HIV-positive applicants. Guaranteed issue (any condition): Mutual of Omaha — best guaranteed issue carrier. An independent agent can shop your specific case to all available carriers to find the best rate.',
  },
  {
    q: 'How much more does high-risk life insurance cost in Indianapolis?',
    a: 'High-risk life insurance costs more than standard life insurance in Indianapolis, but the exact amount depends on your specific condition and how well it\'s controlled. Here are typical rate increases: Controlled high blood pressure: 15–40% above standard rates. Controlled Type 2 diabetes: 40–100% above standard rates. Controlled Type 1 diabetes: 75–200% above standard rates. Stable heart disease (2+ years): 100–300% above standard rates. Cancer in remission (5+ years): 50–200% above standard rates. Obesity (BMI 35–40): 25–75% above standard rates. Even with a table rating, life insurance is often much more affordable than people expect. A 40-year-old with controlled Type 2 diabetes might pay $83–$110/month for $500K 20-year term — still very affordable for the protection it provides.',
  },
  {
    q: 'What if I was declined for life insurance in Indianapolis?',
    a: 'If you were declined for life insurance in Indianapolis, you still have options: 1. Get a second opinion from an independent agent. Different carriers have very different underwriting guidelines. A condition that causes a decline at one carrier may be approved at another. 2. Try simplified issue life insurance. No medical exam, limited health questions. Many conditions that cause declines for traditional coverage are approved for simplified issue. 3. Apply for guaranteed issue life insurance. No health questions, guaranteed approval for all Indianapolis applicants ages 45–85. Coverage up to $25,000. 4. Wait and reapply. If your condition improves (better A1C, weight loss, cancer remission), you may qualify for better rates in the future. 5. Consider a graded benefit policy. Some carriers offer policies with a 2-year waiting period for high-risk applicants. Call (317) 648-2983 — we specialize in finding coverage for Indianapolis residents who have been declined elsewhere.',
  },
  {
    q: 'Can I get life insurance in Indianapolis if I have a hazardous occupation?',
    a: 'Yes — Indianapolis residents with hazardous occupations can get life insurance, though rates may be higher. Common hazardous occupations and their impact: Police officers / firefighters: Standard to Standard Plus rates — most carriers accept these occupations. Construction workers: Standard rates — minimal impact for most construction jobs. Commercial pilots: Standard Plus to Table 2 — depends on flight hours and aircraft type. Miners / oil rig workers: Table 2–4 — higher risk due to occupational hazards. Loggers / tree trimmers: Table 2–4 — one of the highest-risk occupations. Military (active duty): Standard rates for most branches — some carriers exclude combat zones. The key: work with an independent agent who knows which carriers are most lenient with your specific occupation. Some carriers specialize in high-risk occupations and offer much better rates than others.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Robert K.',
    location: 'Indianapolis, IN',
    quote: 'Had a heart attack 3 years ago and was told I was uninsurable. Benjamin found me Prudential at Table 4 — $110/month for $500K. I thought I\'d never get coverage again. Incredible.',
    stars: 5,
    coverage: '$500K 20-year term',
    condition: 'Heart attack survivor',
  },
  {
    name: 'Maria L.',
    location: 'Carmel, Indianapolis',
    quote: 'Type 1 diabetic since age 12. Every carrier I tried declined me. Benjamin found Pacific Life at Table 4 — $95/month for $500K. My family is finally protected.',
    stars: 5,
    coverage: '$500K 20-year term',
    condition: 'Type 1 diabetes',
  },
  {
    name: 'James T.',
    location: 'Fishers, Indianapolis',
    quote: 'Cancer survivor — 6 years in remission. Benjamin found me Prudential at Standard rates. I expected to pay double. Ended up paying $55/month for $500K. Couldn\'t believe it.',
    stars: 5,
    coverage: '$500K 20-year term',
    condition: 'Cancer survivor (6 yrs remission)',
  },
  {
    name: 'Susan H.',
    location: 'Noblesville, Indianapolis',
    quote: 'BMI of 42 and high blood pressure. Was declined twice. Benjamin found me Banner Life at Table 2 — $75/month for $500K. He knew exactly which carrier to use for my situation.',
    stars: 5,
    coverage: '$500K 20-year term',
    condition: 'Obesity + high blood pressure',
  },
];

export default function HighRiskLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'High Risk Life Insurance Indianapolis IN | Health Conditions Accepted | Hoosier Life Insurance';
    const description = 'High risk life insurance in Indianapolis, IN. Diabetes, heart disease, cancer, COPD — all considered. Table ratings explained. Guaranteed issue available. Call (317) 648-2983.';
    const keywords = 'high risk life insurance Indianapolis, life insurance health conditions Indianapolis, life insurance declined Indianapolis, table rating life insurance Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/a97b4179357e523a41f0aa34e72fac5a.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'High Risk Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">Life Insurance Indianapolis</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">High Risk Life Insurance</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20compassionate%20life%20insurance%20agent%20helping%20senior%20couple%20with%20health%20conditions%20warm%20office%20natural%20light%20supportive%20professional%20consultation%20photorealistic%20warm%20neutral%20tones%20cream%20beige%20wide%20angle&width=1440&height=700&seq=highrisk-li-indy-hero-2025a&orientation=landscape"
            alt="High Risk Life Insurance Indianapolis Indiana — Health Conditions Accepted"
            title="High Risk Life Insurance in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-heart-pulse-line text-sm"></i>
              Indianapolis &amp; Marion County · 15 Conditions Covered · Table Ratings Explained · Guaranteed Issue Available
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              High Risk Life Insurance —{' '}
              <span className="text-[#D97642]">Get Covered in Indianapolis</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Diabetes, heart disease, cancer, COPD, obesity — <strong className="text-white">most Indianapolis residents with health conditions can still get life insurance</strong>. We specialize in finding coverage for applicants who have been declined elsewhere.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '15+', label: 'High-risk conditions we help Indianapolis residents cover' },
                { value: '5', label: 'Specialized carriers for high-risk Indianapolis applicants' },
                { value: 'GI', label: 'Guaranteed issue available — no health questions, guaranteed approval' },
                { value: 'Free', label: 'High-risk case review — no obligation, no pressure' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setShowBookingModal(true)} className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-calendar-check-line text-lg"></i>
                Get My High-Risk Rate
              </button>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '15+', label: 'High-risk conditions we help Indianapolis residents cover with life insurance' },
              { value: '5', label: 'Specialized carriers for high-risk Indianapolis applicants — each with different strengths' },
              { value: 'Table 1–10', label: 'Table rating system — each table adds ~25% to standard premium' },
              { value: 'GI Option', label: 'Guaranteed issue available for any condition — no health questions asked' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">
          <div>

            {/* Conditions Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">High-Risk Conditions Guide</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                15 High-Risk Conditions — Indianapolis Life Insurance Options
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Most Indianapolis residents with health conditions can still get life insurance. Here's how each condition affects your rates and which carrier is best:
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="bg-[#1A3A52]">
                      <th className="px-5 py-3 text-left text-xs font-bold text-white/70 uppercase tracking-wide">Condition</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-white/70 uppercase tracking-wide">Rate Impact</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-white/70 uppercase tracking-wide">Best Carrier</th>
                      <th className="px-5 py-3 text-center text-xs font-bold text-white/70 uppercase tracking-wide">Option</th>
                    </tr>
                  </thead>
                  <tbody>
                    {HIGH_RISK_CONDITIONS.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-5 py-4 text-sm font-semibold text-[#1A3A52]">{row.condition}</td>
                        <td className={`px-5 py-4 text-xs text-center font-bold ${row.rateImpact.includes('Decline') ? 'text-red-500' : 'text-[#D97642]'}`}>{row.rateImpact}</td>
                        <td className="px-5 py-4 text-xs text-center text-gray-600">{row.bestCarrier}</td>
                        <td className="px-5 py-4 text-xs text-center text-gray-600">{row.option}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">Rate impacts are estimates. Actual rates depend on severity, control, and carrier underwriting. Call (317) 648-2983 for your exact rate.</p>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Residents Who Got Coverage Despite Health Conditions
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.condition}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                High Risk Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug"><a id={`faq-highrisk-indy-${i}`}>{faq.q}</a></h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-heart-pulse-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">High-Risk Case Review</h3>
                    <p className="text-xs text-white/70">Free · No obligation · All conditions welcome</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Case Review
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All health conditions welcome</p>
              </div>
            </div>

            {/* Table Rating Guide */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-bar-chart-line text-[#D97642]"></i>
                Table Rating Guide
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { table: 'Standard', surcharge: '+0%', example: 'No health conditions' },
                  { table: 'Table 1', surcharge: '+25%', example: 'Mild controlled conditions' },
                  { table: 'Table 2', surcharge: '+50%', example: 'Moderate controlled conditions' },
                  { table: 'Table 4', surcharge: '+100%', example: 'Significant health history' },
                  { table: 'Table 6', surcharge: '+150%', example: 'Serious health conditions' },
                  { table: 'Table 8', surcharge: '+200%', example: 'Very high risk conditions' },
                  { table: 'Table 10', surcharge: '+250%', example: 'Highest risk — or decline' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-xs font-semibold text-[#1A3A52]">{row.table}</div>
                      <div className="text-xs text-gray-400">{row.example}</div>
                    </div>
                    <span className={`text-sm font-bold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#D97642]'}`}>{row.surcharge}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Each table adds ~25% to the standard premium. An independent agent shops all carriers to find the lowest table rating for your condition.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free High-Risk Case Review</h3>
                    <p className="text-xs text-white/70">All conditions welcome · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Case Review',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Health condition or coverage questions? (Optional)',
                    submitButton: 'Confirm My Free Case Review', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Don't assume you're uninsurable — most Indianapolis residents with health conditions can get coverage
          </h2>
          <p className="text-base text-white/75 mb-7">
            We specialize in finding life insurance for Indianapolis residents who have been declined elsewhere. Free case review, no obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => setShowBookingModal(true)} className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line text-lg"></i>
              Get My High-Risk Rate
            </button>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={138} productName="High Risk Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="High Risk Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">High Risk Life Insurance — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
